import { Injectable, inject } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Status } from '@df/core/status';
import { CreditCard } from '@df/session/api/credit-cards/credit-card';
import compact from 'lodash-es/compact';
import { NSagepay } from '../sagepay.namespace';

declare global {
    interface Window {
        sagepayOwnForm?: NSagepay.OwnForm;
    }
}

@Injectable({
    providedIn: 'root'
})
export abstract class SagepayPiService {
    generalErrorMessage = Checkout.GENERAL_PAYMENT_ERROR_MESSAGE;

    private request = inject(RequestService);

    /**
     * Sends request for sagepay api merchant key required to tokenize card details
     * @param {Status} status
     * @returns {Promise<NSagepay.IMerchantKeyResponse>}
     */
    generateMerchantKey(status?: Status): Promise<NSagepay.IMerchantKeyResponse> {
        return this.request.send(status, { path: NSagepay.Endpoints.METCHANT_KEY });
    }

    /**
     * Returns browser info required for card holder verification
     * @returns {NSagepay.IBrowserInfo}
     */
    getBrowserInfo(): NSagepay.IBrowserInfo {
        const browserInfo: NSagepay.IBrowserInfo = {
            javascript_enabled: 1,
            accept_headers: 'Accept headers.',
            timezone: new Date().getTimezoneOffset()
        };

        if (typeof window !== 'undefined') {
            const navigator = window.navigator;
            const screen = window.screen;

            if (navigator) {
                browserInfo.language = navigator.language;
                browserInfo.user_agent = navigator.userAgent;
                browserInfo.java_enabled = navigator.javaEnabled() ? 1 : 0;
            }

            if (screen) {
                browserInfo.color_depth = screen.colorDepth;
                browserInfo.screen_width = screen.width;
                browserInfo.screen_height = screen.height;
            }
        }

        return browserInfo;
    }

    //////////////////////////////////////////
    /////
    /////   Own form methods
    /////
    //////////////////////////////////////////

    /**
     * Returns sagepay own form constructor
     * @returns {NSagepay.OwnForm}
     */
    getOwnForm(): NSagepay.OwnForm | undefined {
        return typeof window === 'undefined' ? undefined : window.sagepayOwnForm;
    }

    /**
     * Tokenise card details
     * @param {string}                                      merchantSessionKey
     * @param {NSagepay.IOwnFormTokeniseOptionsCardDetails}  cardDetails
     * @returns {Promise<NSagepay.IOwnFormTokeniseResult>}
     */
    tokeniseOwnFormCardDetails(
        merchantSessionKey: string,
        cardDetails: NSagepay.IOwnFormTokeniseOptionsCardDetails
    ): Promise<NSagepay.ITokeniseResult> {
        return new Promise((resolve, reject) => {
            const ownForm = this.getOwnForm();
            if (ownForm) {
                try {
                    ownForm({ merchantSessionKey }).tokeniseCardDetails({
                        cardDetails,
                        onTokenised: result => {
                            if (result.success && result.cardIdentifier) {
                                resolve(result);
                            } else {
                                reject(this.getError(result));
                            }
                        }
                    });
                } catch (e) {
                    reject(this.getError());
                }
            } else {
                reject(this.getError());
            }
        });
    }

    /**
     * Returns tokenise saved card details
     * @param {string}      merchantKey
     * @param {CreditCard}  card
     * @param {string}      cvc
     */
    tokeniseReusableCard(merchantKey: string, card: CreditCard, cvc: string = '000'): Promise<NSagepay.ITokeniseResult> {
        return new Promise((resolve, reject) => {
            if (merchantKey && card && cvc) {
                const securityCode = cvc.replace(/\D/g, '');
                const reusableCardData: NSagepay.IGetReusableCard = {};
                if (card?.data?.public_hash) {
                    reusableCardData.public_hash = card.data.public_hash;
                } else if (card?.data?.id) {
                    reusableCardData.card_id = card.data.id;
                }
                this.request
                    .send(
                        undefined,
                        { path: NSagepay.Endpoints.GET_REUSABLE_CARD, data: reusableCardData },
                        (tokeniseResult: NSagepay.ITokeniseResult) => {
                            if (tokeniseResult.cardIdentifier && tokeniseResult.cardType) {
                                this.activateReusableCardIdentifier(merchantKey, tokeniseResult.cardIdentifier, securityCode)
                                    .then(() => {
                                        resolve(tokeniseResult);
                                    })
                                    .catch(e => {
                                        reject(e);
                                    });
                            } else {
                                reject(tokeniseResult);
                            }
                        }
                    )
                    .catch(reject);
            } else {
                reject(this.getError());
            }
        });
    }

    /**
     * Activates resusable card
     * @param {string}  merchantKey
     * @param {string}  cardIdentifier
     * @param {string}  securityCode
     */
    protected activateReusableCardIdentifier(
        merchantKey: string,
        cardIdentifier: string,
        securityCode: string
    ): Promise<NSagepay.IActivateReusableCardIdentifierResult> {
        return new Promise((resolve, reject) => {
            const ownForm = this.getOwnForm();
            if (ownForm) {
                try {
                    ownForm({ merchantSessionKey: merchantKey }).activateReusableCardIdentifier({
                        reusableCardIdentifier: cardIdentifier,
                        securityCode,
                        onActivated: result => {
                            if (result?.success) {
                                resolve(result);
                            } else {
                                reject(result);
                            }
                        }
                    });
                } catch (e) {
                    reject(this.getError());
                }
            } else {
                reject(this.getError());
            }
        });
    }

    /**
     * Normalizes error message
     * @param {NSagepay.IOwnFormTokeniseError} result
     * @returns {string}
     */
    getError(result?: NSagepay.ITokeniseResult | string): string {
        if (typeof result === 'string') {
            return result;
        } else if (result?.errors?.length) {
            return compact(result.errors.map(error => error.clientMessage || error.message)).join('. ');
        } else {
            return result?.error || this.generalErrorMessage;
        }
    }
}